/**
 * Helpers puros do formulário de novo caso (extraídos de NovoCasoPage em
 * E23.F3 p/ manter o componente enxuto e testável). Sem JSX, sem hooks.
 */
import { type AxiosInstance } from 'axios';
import { ptBR } from '@/i18n/pt-BR';
import {
  CaseError,
  MODALIDADE_DEFAULT,
  type CaseCreateData,
  type CaseRecordData,
  type CaseUpdateData,
  type Modalidade,
} from '@/lib/cases';
import { type CcbMetadataData, DocumentError, uploadDocument } from '@/lib/case_source_documents';

export const CNJ_REGEX = /^\d{7}-\d{2}\.\d{4}\.\d\.\d{2}\.\d{4}$/;
export const CNJ_PLACEHOLDER = '—';
// E9.F2.T12: CPF aceita formato com ou sem máscara (apenas dígitos, pontos
// e hífen). Sem validação de dígito verificador — backend tem CHECK constraint
// equivalente; perito pode estar registrando um CPF informalmente passado.
export const CPF_REGEX = /^[0-9.-]+$/;

export const ESPECIME_OPTIONS = [
  { value: 'Análise do CCB', label: ptBR.casos.especimeCcb },
  { value: 'Contrato', label: ptBR.casos.especimeContrato },
  { value: 'Cheque', label: ptBR.casos.especimeCheque },
  { value: 'Procuração', label: ptBR.casos.especimeProcuracao },
  { value: 'Testamento', label: ptBR.casos.especimeTestamento },
  { value: 'Aval em nota promissória', label: ptBR.casos.especimeAval },
  { value: 'Termo de quitação', label: ptBR.casos.especimeQuitacao },
  { value: 'Endosso', label: ptBR.casos.especimeEndosso },
  { value: 'Outro', label: ptBR.casos.especimeOutro },
] as const;

/** E74.F1 — opções do seletor de modalidade (ADR-0075 §1), na ordem de exibição. */
export const MODALIDADE_OPTIONS: readonly { value: Modalidade; label: string }[] = [
  { value: 'grafotecnica', label: ptBR.casos.modalidadeGrafotecnica },
  { value: 'eletronica', label: ptBR.casos.modalidadeEletronica },
];

export interface FormState {
  title: string;
  /** E74.F1 — escolhida na criação; no caso existente o campo fica travado. */
  modalidade: Modalidade;
  requester: string;
  processNumber: string;
  especime: string;
  parties: string;
  receivedAt: string;
  deadline: string;
  cpf: string;
  contrato: string;
}

export interface FieldErrors {
  title?: string;
  processNumber?: string;
  deadline?: string;
  receivedAt?: string;
  cpf?: string;
}

export function todayIsoDate(): string {
  const now = new Date();
  const yyyy = now.getFullYear();
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const dd = String(now.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

export function validate(state: FormState): FieldErrors {
  const errors: FieldErrors = {};
  if (state.title.trim().length === 0) errors.title = ptBR.casos.errorMissingTitle;
  if (state.receivedAt.length === 0) errors.receivedAt = ptBR.casos.errorMissingReceivedAt;
  const proc = state.processNumber.trim();
  if (proc.length > 0 && proc !== CNJ_PLACEHOLDER && !CNJ_REGEX.test(proc)) {
    errors.processNumber = ptBR.casos.errorInvalidCnj;
  }
  if (state.deadline.length > 0 && state.deadline < state.receivedAt) {
    errors.deadline = ptBR.casos.errorDeadlineBeforeReceived;
  }
  const cpf = state.cpf.trim();
  if (cpf.length > 0 && !CPF_REGEX.test(cpf)) {
    errors.cpf = ptBR.casos.errorInvalidCpf;
  }
  return errors;
}

/**
 * Hidrata o `FormState` a partir de um caso persistido (E33.F4 — passo
 * "Caso" editável do wizard). Colunas nulas viram string vazia para o input
 * controlado; o `buildPayload` reconverte vazio→null no save.
 */
export function formStateFromCase(record: CaseRecordData): FormState {
  return {
    title: record.title,
    modalidade: record.modalidade ?? MODALIDADE_DEFAULT,
    requester: record.requester ?? '',
    processNumber: record.process_number ?? '',
    especime: record.especime ?? '',
    parties: record.parties ?? '',
    receivedAt: record.received_at,
    deadline: record.deadline ?? '',
    cpf: record.cpf ?? '',
    contrato: record.contrato ?? '',
  };
}

export function buildPayload(state: FormState): CaseCreateData {
  const proc = state.processNumber.trim();
  const cpf = state.cpf.trim();
  const contrato = state.contrato.trim();
  return {
    title: state.title.trim(),
    modalidade: state.modalidade,
    requester: state.requester.trim().length > 0 ? state.requester.trim() : null,
    process_number: proc.length > 0 ? proc : null,
    especime: state.especime.length > 0 ? state.especime : null,
    parties: state.parties.trim().length > 0 ? state.parties.trim() : null,
    received_at: state.receivedAt,
    deadline: state.deadline.length > 0 ? state.deadline : null,
    cpf: cpf.length > 0 ? cpf : null,
    contrato: contrato.length > 0 ? contrato : null,
  };
}

/**
 * Payload do PATCH (E33.F4): os mesmos campos da criação, menos a modalidade —
 * ela é fixada na criação e o servidor recusa o campo na atualização (E74.F1).
 */
export function buildUpdatePayload(state: FormState): CaseUpdateData {
  const { modalidade: _modalidade, ...payload } = buildPayload(state);
  void _modalidade;
  return payload;
}

/**
 * Aplica os metadados propostos da CCB ao formulário (E23.F3). Só sobrescreve
 * campos com valor detectado (não apaga o que o perito já digitou).
 * Mapeamento: cpf→cpf · process_number→processo · contract→contrato ·
 * author→requester · defendant→parties.
 */
export function applyCcbMetadata(state: FormState, meta: CcbMetadataData): FormState {
  return {
    ...state,
    cpf: meta.cpf ?? state.cpf,
    processNumber: meta.process_number ?? state.processNumber,
    contrato: meta.contract ?? state.contrato,
    requester: meta.author ?? state.requester,
    parties: meta.defendant ?? state.parties,
  };
}

/**
 * Anexa a CCB importada como contestada após criar o caso (E23.F3.T06).
 * Retorna chave de erro neutra se o anexo falhar (caso já criado); `null` em
 * sucesso. Isola a lógica p/ manter o submit do componente simples.
 */
export async function attachImportedCcb(
  client: AxiosInstance,
  caseId: string,
  file: File,
): Promise<string | null> {
  try {
    await uploadDocument(client, caseId, { file, kind: 'contested' });
    return null;
  } catch (err) {
    if (err instanceof DocumentError) return ptBR.casos.importCcbAttachFailed;
    throw err;
  }
}

export function mapErrorToMessage(err: unknown): string {
  if (err instanceof CaseError) {
    if (err.reason === 'validation') return ptBR.casos.errorValidation;
    if (err.reason === 'network') return ptBR.casos.errorNetwork;
  }
  return ptBR.casos.errorGeneric;
}
